/**
 * The chrome constants the top bar, the rail and the tab bar are built from.
 *
 * Lifted out of the prototype with the components that use them, so the
 * shipped portal draws from the same numbers the design was settled on rather
 * than from an approximation of them. The rest of the prototype's `chrome.ts`
 * is device-frame geometry — simulated phone bezels and status bars — which
 * has no meaning outside `/prototypes`, so it stays there.
 */

/** The tab bar's height, before whatever the device owes the bottom edge. */
export const TABBAR_H = 64;

/**
 * Header heights, so the scroll area can reserve them.
 *
 * The header is taller at desk width and its title bigger. On a phone 56
 * points and a 17px title is right — the bar is competing with a status bar
 * above it and a thumb's worth of content below. At 1400 the same bar is a
 * thin strip with a small bold label floating in it, which is what made the
 * page look like parts placed rather than a page with a title.
 */
export const HEADER_H = 56;
export const HEADER_WIDE_H = 76;

/**
 * What the footer occupies, so the loading shell can leave room for it.
 *
 * The footer is not in the skeleton: it needs a real support address, and an
 * invented one is worse than a gap. But it is in the shell, below `main`, and
 * a page that centres itself vertically centres inside whatever `main` was
 * left. Without this the skeleton centred in a taller column than the real
 * screen and every block sat about sixty points low, then jumped up when the
 * content arrived.
 *
 * Measured from the rendered footer, and the same at both widths: the links
 * sit beside the question on a desk and under it on a phone, which costs the
 * same height either way.
 */
export const FOOTER_H = 121;

/**
 * Floating chrome.
 *
 * Content scrolls under the header and the tab bar rather than being clipped
 * above them, which is what makes a phone screen feel taller than it is. The
 * blur is what keeps text legible as it passes underneath; without it a
 * translucent bar is just illegible text at 65% opacity.
 *
 * THE FILL AND THE EDGE ARE WHAT MAKE IT READ AS GLASS, not the blur. At 80%
 * over a dark card there is nothing left to see through — the material was
 * only legible in the prototype because colourful video thumbnails happened to
 * sit behind it, and on a page of dark cards the same bar reads as a flat
 * slab. Two changes fix that on any content: less fill with more blur to keep
 * the text legible through it, and a hairline on the top edge, which is the
 * light catching the near side of a physical material and the cue that says
 * "surface above content" when the two are a similar colour.
 *
 * `surface-overlay-ring` rather than the card's, because this is overlay
 * chrome: it is the brighter of the two, and it is the one that inverts to a
 * dark hairline on the light theme.
 */
export const CHROME =
  "bg-nf-ds-surface-ground/65 backdrop-blur-2xl backdrop-saturate-150 border-t border-nf-ds-surface-overlay-ring [@media(prefers-reduced-transparency:reduce)]:bg-nf-ds-surface-ground [@media(prefers-reduced-transparency:reduce)]:backdrop-filter-none";

/**
 * Press feedback for a whole row or tile.
 *
 * On pointer-down, not on release — a row that highlights only once the
 * navigation resolves reads as a dropped tap, and on a phone the navigation is
 * the slowest part. Scale is gentler than a button's 0.97 because the target
 * is the width of the screen and 3% of 358px is a visible lurch.
 *
 * A WASH, NOT A SURFACE. This used to press to `surface-ground`, which is a
 * fixed colour and therefore wrong in two directions at once. On a card it is
 * darker than the card, so a pressed row read as a hole punched through it —
 * and with the scale pulling the row in, the card showed around the edge as a
 * border that appeared on touch. In the rail it is the background, so a nav
 * row pressed to exactly the colour it already was and gave no feedback at
 * all.
 *
 * Six percent of the foreground has neither problem: it lightens on dark,
 * darkens on light, and it is a wash over whatever the row is actually
 * sitting on rather than an assumption about it.
 */
export const ROW_PRESS =
  // `scale` named explicitly: Tailwind v4's `scale-*` is the `scale`
  // property, which a list naming only `transform` does not animate.
  "cursor-pointer transition-[background-color,transform,scale] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.99] active:bg-nf-ds-fg/[0.06] motion-reduce:transition-none motion-reduce:active:scale-100";

/**
 * 48px, and it is not a preference.
 *
 * The shipped portal's nav items were `h-8` — 32px — which is under every
 * published minimum: 44pt on iOS, 48dp on Android, 44px in WCAG 2.5.5. 48
 * rather than 44 because these are hit with a thumb at the bottom edge of the
 * screen, the least accurate reach on the device.
 *
 * The prototype steps this down past its frame's widest breakpoint; here the
 * equivalent is `lg`, the width at which the rail replaces the tab bar.
 */
export const TOUCH = "min-h-[48px] lg:min-h-[44px]";

/**
 * 16px prose, against the 14px body role.
 *
 * The phone is held ~30cm away against ~55cm for a monitor, so the same
 * angular size needs more CSS pixels; 16px is also the size below which iOS
 * Safari zooms the viewport on focusing an input, which reflows the whole
 * screen mid-typing. Past rail width it steps back to the surface's own 14px —
 * neither reason survives a pointer at a desk.
 */
export const PROSE = "text-[16px] leading-[1.45] lg:text-[14px]";

/**
 * Optical correction for a section that opens with a heading.
 *
 * A gap between two cards ends on a fill, which starts exactly at the box
 * edge. A gap above a heading ends on the cap of a letter, and that cap is not
 * at the top of its box: the `section` role is 20px type in a 24px line box
 * with a 14.3px cap height, so the first ink lands 5.7 points down. Equal
 * numbers therefore produce unequal space — the heading looks about a tenth
 * further from the block above it than the block above that.
 *
 * Six points back is what makes 48 of layout read as 48 of air. It is an
 * arbitrary value on purpose and one of the few that should be: the DS spacing
 * roles describe distances between boxes, and this is the difference between a
 * box and the ink inside it, which no step on that scale can express. It is
 * derived rather than eyeballed — measure the role's line box against its cap
 * height and the number falls out — so it moves only if the role does.
 *
 * Only for a section whose first child is text. A section opening with a card
 * needs no correction, and giving it one would pull it 6 points too close.
 *
 * The mirror case exists and is not corrected here: a gap *below* a text block
 * is short by the descender space in the same way, which is why the page
 * header sits a little tighter to what follows it than two cards do. One
 * correction at a time, where it was actually visible.
 */
export const SECTION_CAP_TRIM = "-mt-[6px]";

/**
 * The element that recedes while a sheet is up.
 *
 * iOS scales the page back and rounds its corners as a sheet rises, and the
 * effect is not decoration: it says the sheet is a layer above a surface
 * rather than a panel pasted onto one, and it is what makes the dark band
 * behind the sheet read as depth instead of as a gap. The design system's
 * `Sheet` takes the id and drives it; the app only has to name the thing.
 *
 * The whole shell, not the scroll area — the header and the tab bar are part
 * of the view that is being suspended, and a page that sinks while its chrome
 * stays put reads as two applications. Their `position: fixed` resolves
 * against the transformed shell rather than the window, so they travel with
 * it, which is exactly what is wanted here.
 */
export const DEPTH_ROOT_ID = "one-depth-root";

/**
 * What an overlay has to wear to belong to this surface.
 *
 * Radix and `react-modal-sheet` both portal their panel to `document.body`,
 * and `/one` wears the candidate layer on a `display: contents` div inside
 * the route — so a portalled panel lands outside it and renders in
 * Framework's palette. On a near-black page that is a violet-grey card
 * arriving over a neutral one, which is exactly as obvious as it sounds.
 *
 * The fill is here too because `--color-popover` is frozen at `:root`: the
 * modal primitive's own `bg-popover` cannot be re-themed by a nested layer,
 * so the surface has to be named rather than inherited.
 *
 * Only for overlays opened from `/one`. An overlay shared with a staff page
 * passes nothing and keeps Framework's colours, which are correct there.
 */
export const PORTAL_OVERLAY =
  "ds-next border-transparent bg-nf-ds-surface-card";

/**
 * The rail's width, and the offset the content column takes to clear it.
 *
 * One place, because the shell and its skeleton both need it and they have
 * to agree — the symptom of two copies drifting is a sliver of content under
 * the nav, which is exactly the kind of thing nobody notices until it ships.
 * `SideRail` states the same number in pixels for its own box and says why.
 */
export const RAIL_W = "15rem";

/**
 * The rail folded to its icons: a 40px row inside 8px of padding each side,
 * which is exactly a 16px glyph centred with the same 12px it has when open.
 */
export const RAIL_W_COLLAPSED = "3.5rem";

/**
 * How the rail folds, shared with the content offset that moves with it.
 *
 * Ease-in-out rather than the press curve: a press wants its response in the
 * first frame, but a column changing width under the reader's eye should
 * start and finish gently, so the page slides rather than lurches.
 */
export const RAIL_MOTION =
  "duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none";

/**
 * A field, in this surface's palette rather than Framework's.
 *
 * `Input` and `Textarea` fill with `bg-background`, and inside `.ds-next` that
 * resolves to Framework's slate-950: a navy field on a neutral page. Not
 * because the candidate layer forgot to re-theme it. `ds-next.css` does set
 * `--background`, but `globals.css` declares the shadcn tokens under a plain
 * `@theme`, so `--color-background`, which is what `bg-background` actually
 * reads, is resolved once at `:root` and inherits frozen. Nothing nested can
 * move it.
 *
 * Two classes are the local patch. The fix is `@theme inline` in
 * `globals.css`, which would make every shadcn-token utility follow the layer
 * it is rendered in and light up the twins `ds-next.css` already declares.
 *
 * The ground, not the card: these sit on a card, and a field painted the same
 * colour as the pane behind it has no edges at all.
 */
export const FIELD = "bg-nf-ds-surface-ground border-nf-ds-surface-card-ring";

/**
 * A quiet button, in this surface's palette rather than Framework's.
 *
 * The same frozen-token problem `FIELD` documents above, on the other half
 * of the DS button. `outline` fills with `bg-background` and draws
 * `border-input`; `ghost` and `outline` both hover to `bg-accent`. Inside
 * `.ds-next` those resolve to Framework's slate-950 and slate-800, so a row
 * of buttons on a neutral near-black card comes out navy, and the one
 * correctly themed button beside them looks like the odd one rather than the
 * three that are wrong.
 *
 * Measured on the Updates card: the outline button painted rgb(2, 8, 24)
 * against a card of rgb(20, 20, 21). Not a different brightness, a different
 * hue.
 *
 * The hover wash is `fg/[0.06]`, the same one the DS select settled on, so
 * it lifts off whatever ground it lands on instead of naming a colour.
 */
export const QUIET_HOVER = "hover:bg-nf-ds-fg/[0.06] hover:text-nf-ds-fg";

/** `QUIET_HOVER`, plus the border and fill an outline button needs. */
export const QUIET_OUTLINE = `bg-transparent border-nf-ds-border ${QUIET_HOVER}`;
